import "@/App.css";
import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { loadBranding } from "@/lib/branding";
import { refreshAppTz } from "@/lib/tz";
import { useIpadKillWatcher } from "@/lib/useIpadKillWatcher";
import ErrorBoundary from "@/components/ErrorBoundary";

import GuestLanding from "@/pages/GuestLanding";
import GuestExperienceHub from "@/pages/GuestExperienceHub";
import GuestModulePage from "@/pages/GuestModulePage";
import GuestMenu from "@/pages/GuestMenu";
import GuestBooking from "@/pages/GuestBooking";
import GuestConfirmation from "@/pages/GuestConfirmation";
import GuestReservations from "@/pages/GuestReservations";
import GuestActivity from "@/pages/GuestActivity";
import GuestVisit from "@/pages/GuestVisit";
import CabinView from "@/pages/CabinView";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";
import SpaOpsView from "@/pages/SpaOpsView";
import SpaDoorDisplay from "@/pages/SpaDoorDisplay";
import MgmtDashboard from "@/pages/MgmtDashboard";
import MobileCrewView from "@/pages/MobileCrewView";
import IpadThemeMount from "@/components/IpadThemeMount";
import CabinLockedOverlay from "@/components/CabinLockedOverlay";
import DevAdvisoryBanner from "@/components/DevAdvisoryBanner";
import AppUpdateGate from "@/components/AppUpdateGate";
import DedicatedCalendarView from "@/pages/DedicatedCalendarView";

function AdminGuard({ children }) {
  const { admin, loading } = useAuth();
  const location = useLocation();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-primary/60">Loading…</div>;
  if (!admin) return <Navigate to="/admin/login" replace />;
  // Spa Ops kiosk accounts are auto-redirected to their dedicated view.
  if (admin.role === "spa_ops") return <Navigate to="/spa-ops" replace />;
  if (admin.role === "spa_door") return <Navigate to="/spa-door" replace />;
  if (admin.role === "management_company") return <Navigate to="/mgmt" replace />;
  // Auto-redirect phone-sized devices landing on the desktop dashboard
  // to the mobile crew view. Session-scoped "force desktop" opt-out lets
  // a supervisor on a tablet override this from the Desktop button on
  // the mobile top bar.
  if (location.pathname === "/admin" && typeof window !== "undefined") {
    let forced = false;
    try { forced = sessionStorage.getItem("yspa:force_desktop") === "1"; } catch { /* ignore */ }
    if (!forced && window.matchMedia && window.matchMedia("(max-width: 767px)").matches) {
      return <Navigate to="/crew" replace />;
    }
  }
  return children;
}

function SpaOpsGuard({ children }) {
  const { admin, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-primary/60">Loading…</div>;
  if (!admin) return <Navigate to="/admin/login" replace />;
  return children;
}

function SpaDoorGuard({ children }) {
  // Any dashboard role can PREVIEW the door display; only spa_door
  // accounts are auto-redirected here by AdminGuard so the crew can
  // walk over to the corridor device without needing a second login.
  const { admin, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-primary/60">Loading…</div>;
  if (!admin) return <Navigate to="/admin/login" replace />;
  return children;
}

function MgmtGuard({ children }) {
  // Only the management_company role should see the /mgmt fleet view.
  // Every other authenticated dashboard user is bounced back to /admin
  // (their normal landing).
  const { admin, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-primary/60">Loading…</div>;
  if (!admin) return <Navigate to="/admin/login" replace />;
  if (admin.role !== "management_company") return <Navigate to="/admin" replace />;
  return children;
}

/** Route-agnostic cabin iPad eviction listener. Must live INSIDE the
 *  <BrowserRouter> so it can use `useNavigate`, but ABOVE `<Routes>`
 *  so it survives every route change — the guest is not always on
 *  CabinView when an admin kicks the device. */
function IpadKillMount() {
  useIpadKillWatcher();
  return null;
}

export default function App() {
  const [brandingReady, setBrandingReady] = useState(false);
  useEffect(() => {
    Promise.all([loadBranding(), refreshAppTz()]).finally(() => setBrandingReady(true));
  }, []);

  if (!brandingReady) {
    return <div className="min-h-screen bg-background flex items-center justify-center text-primary/50 overline">Loading…</div>;
  }

  return (
    <div className="App">
      <ErrorBoundary>
        <AuthProvider>
          <BrowserRouter>
            <Toaster position="top-right" richColors closeButton />
            <IpadKillMount />
            <IpadThemeMount />
            <CabinLockedOverlay />
            <DevAdvisoryBanner />
            <AppUpdateGate />
            <Routes>
              <Route path="/" element={<GuestLanding />} />
              <Route path="/experience" element={<GuestExperienceHub />} />
              <Route path="/service/:moduleId" element={<GuestModulePage />} />
              <Route path="/menu" element={<GuestMenu />} />
              <Route path="/book/:treatmentId" element={<GuestBooking />} />
              <Route path="/confirmation" element={<GuestConfirmation />} />
              <Route path="/reservations" element={<GuestReservations />} />
              <Route path="/journey" element={<GuestActivity />} />
              <Route path="/visit/:token" element={<GuestVisit />} />
              <Route path="/cabin" element={<CabinView />} />
              <Route path="/calendar-device/:token" element={<DedicatedCalendarView />} />
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin" element={<AdminGuard><AdminDashboard /></AdminGuard>} />
              <Route path="/crew" element={<AdminGuard><MobileCrewView /></AdminGuard>} />
              <Route path="/spa-ops" element={<SpaOpsGuard><SpaOpsView /></SpaOpsGuard>} />
              <Route path="/spa-door" element={<SpaDoorGuard><SpaDoorDisplay /></SpaDoorGuard>} />
              <Route path="/mgmt" element={<MgmtGuard><MgmtDashboard /></MgmtGuard>} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
        </AuthProvider>
      </ErrorBoundary>
    </div>
  );
}
